import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Mail, HelpCircle, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { motion } from 'framer-motion';

export const HamburgerMenu = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const openChatbot = () => {
    if (window.voiceflow && window.voiceflow.chat) {
      window.voiceflow.chat.open();
      onClose();
    }
  };

  const cardVariants = {
    initial: { opacity: 0, x: 20 },
    animate: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.3,
      },
    }),
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/90 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Sidebar - Responsive width */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[400px] md:w-[500px] bg-zinc-950 border-l border-zinc-800 z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 sm:p-8 border-b border-zinc-800">
            <h2 className="text-xl sm:text-2xl font-bold text-white">{t('menu.title')}</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-gray-400 hover:text-white hover:bg-zinc-800 transition-colors h-12 w-12"
            >
              <X className="h-7 w-7" />
            </Button>
          </div>

          {/* Menu Items */}
          <div className="flex-1 p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 overflow-y-auto">
            <motion.a
              href="mailto:info@digitalperformances.it"
              className="flex items-center gap-3 sm:gap-5 p-4 sm:p-6 rounded-none bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-700 transition-all group"
              custom={0}
              initial="initial"
              animate={isOpen ? 'animate' : 'initial'}
              variants={cardVariants}
            >
              <div className="p-4 rounded-none bg-white/5 group-hover:bg-white/10 transition-colors">
                <Mail className="h-8 w-8 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{t('menu.emailLabel')}</p>
                <p className="text-white font-medium text-lg">info@digitalperformances.it</p>
              </div>
            </motion.a>

            <motion.a
              href="mailto:assistenza@digitalperformances.it"
              className="flex items-center gap-5 p-6 rounded-none bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-700 transition-all group"
              custom={1}
              initial="initial"
              animate={isOpen ? 'animate' : 'initial'}
              variants={cardVariants}
            >
              <div className="p-4 rounded-none bg-white/5 group-hover:bg-white/10 transition-colors">
                <HelpCircle className="h-8 w-8 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{t('menu.supportLabel')}</p>
                <p className="text-white font-medium text-lg">assistenza@digitalperformances.it</p>
              </div>
            </motion.a>

            <motion.button
              onClick={openChatbot}
              className="w-full flex items-center gap-5 p-6 rounded-none bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-700 transition-all group"
              custom={2}
              initial="initial"
              animate={isOpen ? 'animate' : 'initial'}
              variants={cardVariants}
            >
              <div className="p-4 rounded-none bg-white/10 group-hover:bg-white/20 transition-colors">
                <MessageSquare className="h-8 w-8 text-white" />
              </div>
              <div className="text-left">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{t('menu.chatLabel')}</p>
                <p className="text-white font-medium text-lg">{t('menu.chatText')}</p>
              </div>
            </motion.button>
          </div>
        </div>
      </div>
    </>
  );
};